import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { API_BASE } from "./site";

// Set axios defaults to send cookies
axios.defaults.withCredentials = true;

// Dev-only logger to avoid noisy console output in production while still
// surfacing unexpected errors during development.
const devError = (...args) => {
  if (process.env.NODE_ENV !== "production") {
    console.error(...args);
  }
};

const AuthContext = createContext({
  user: null,
  loading: true,
  refresh: async () => {},
  logout: async () => {},
  exchangeSession: async () => {},
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/auth/me`);
      setUser(res.data);
    } catch (err) {
      // Expected when no session exists — only log unexpected (non-401) errors.
      if (err?.response?.status && err.response.status !== 401) {
        devError("auth.refresh failed", err);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [setUser, setLoading]);

  const exchangeSession = useCallback(async (sessionId) => {
    const res = await axios.post(`${API_BASE}/auth/google-session`, {
      session_id: sessionId,
    });
    setUser(res.data);
    setLoading(false);
    return res.data;
  }, [setUser, setLoading]);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_BASE}/auth/logout`);
    } catch (err) {
      devError("auth.logout failed", err);
    }
    setUser(null);
  }, [setUser]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ user, loading, refresh, logout, exchangeSession }),
    [user, loading, refresh, logout, exchangeSession]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
